import React, { useState } from 'react';
import { Box, Grid, Link, Stack, Switch, Tab, Tabs, Typography } from '@mui/material';
import Container from 'components/Container';
import PlanBlock from './components/PlanBlock';
import PlansLearnMoreLink from './components/PlansLearnMoreLink';
import { useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { push, Routes, useTypedLocation } from 'routing';
import { useTranslation } from 'services/localization';
import { Link as RRLink } from 'react-router-dom';
import { isLitePlan, Plan } from 'common/modules/plans';
import RequestYearlyQuotaModal from './components/RequestYearlyQuotaModal';
import { getSubscriptionDurationOptions } from './utils';

const ChangePlan = () => {
	const shopPlan = useSelector(ShopSelectors.shopPlan);
	const shopBillingCycle = useSelector(ShopSelectors.shopBillingCycle);
	const { t } = useTranslation();
	const [overThousandMR, setOverThousandMR] = useState<boolean>(false);
	const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
	const [isRequestYearlyQuotaModalOpen, setIsRequestYearlyQuotaModalOpen] = useState<boolean>(
		false,
	);
	const [currentPlan, setCurrentPlan] = useState<Plan | undefined>(shopPlan);
	const { state } = useTypedLocation();

	const subscriptionDurationOptions = getSubscriptionDurationOptions(t);

	const handlePlanClick = (plan: Plan) => {
		if (subscriptionDurationOptions[activeTabIndex].duration > 1) {
			setIsRequestYearlyQuotaModalOpen(true);
			setCurrentPlan(plan);
		} else {
			push(Routes.PlanConfirm(plan), state);
		}
	};

	const renderTabName = (name: string, description?: string) => {
		return description ? (
			<p style={{ margin: 0 }}>
				{name} <span style={{ fontSize: 12, fontWeight: 400 }}>{`(${description})`}</span>
			</p>
		) : (
			<p style={{ margin: 0 }}>{name}</p>
		);
	};

	return (
		<>
			<Container>
				<Stack direction="column" alignItems="center" spacing={3}>
					<Typography variant="h4" textAlign="center" sx={{ mt: 1 }}>
						{isLitePlan(shopPlan)
							? t('plans.upgradeTitle', 'Upgrade plan')
							: t('plans.pageTitle', 'Change plan')}
					</Typography>
					<Typography variant="body1" textAlign="center" sx={{ maxWidth: 500 }}>
						{subscriptionDurationOptions[activeTabIndex].header}
					</Typography>
				</Stack>
				<Stack direction="column" alignItems="center" spacing={1}>
					<Typography variant="h6" textAlign="center" sx={{ mt: 3 }}>
						{t('plans.yourMonthlyRevenue', 'Your monthly revenue')}
					</Typography>
					<Stack direction="row" alignItems="center" justifyItems="center">
						<Typography variant="body1">{'$0-1000'}</Typography>
						<Switch
							checked={overThousandMR}
							disabled={activeTabIndex !== 0}
							onChange={() => setOverThousandMR(!overThousandMR)}
							sx={{
								'& .MuiSwitch-switchBase': {
									color: (theme) => theme.palette.primary.main,
									'&.Mui-checked': {
										color: (theme) => theme.palette.primary.main,
									},
									'&.Mui-disabled': {
										color: (theme) => theme.palette.primary.light,
										opacity: 0.8,
									},
								},
								'& .MuiSwitch-switchBase.Mui-checked': {
									color: (theme) => theme.palette.primary.main,
									'&.Mui-disabled': {
										color: (theme) => theme.palette.primary.light,
										opacity: 0.8,
									},
								},
								'& .MuiSwitch-switchBase + .MuiSwitch-track': {
									backgroundColor: (theme) => theme.palette.primary.light,
									'&.Mui-checked': {
										backgroundColor: (theme) => theme.palette.primary.light,
									},
								},
								'& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
									backgroundColor: (theme) => theme.palette.primary.light,
								},
							}}
						/>
						<Typography variant="body1">{'$1000+'}</Typography>
					</Stack>
				</Stack>
				<Box display="flex" justifyContent="center">
					<Tabs
						sx={{
							margin: '0 auto',
							mt: 4,
							padding: 1,
							backgroundColor: (theme) => theme.palette.common.white,
							border: (theme) => `1px solid ${theme.palette.divider}`,
							borderRadius: 0.5,
						}}
						TabIndicatorProps={{
							sx: {
								display: 'none',
							},
						}}
						value={activeTabIndex}
					>
						{subscriptionDurationOptions.map((tab, index) => (
							<Tab
								key={tab.name}
								value={index}
								onClick={() => {
									setActiveTabIndex(index);
									setOverThousandMR(true);
								}}
								sx={{
									fontSize: 16,
									color: (theme) => theme.palette.common.black,
									borderRadius: 0.5,
									textTransform: 'none',
									padding: 1,
									'&.Mui-selected': {
										backgroundColor: (theme) => theme.palette.primary.lightest,
										color: (theme) => theme.palette.primary.dark,
									},
								}}
								label={renderTabName(tab.name, tab.description)}
							/>
						))}
					</Tabs>
				</Box>

				<Grid container spacing={2} sx={{ mt: 2 }} justifyContent="center" alignItems="stretch">
					<Grid item xs={12} md={6} lg={4}>
						<PlanBlock
							plan="BASIC"
							active={
								shopPlan === 'BASIC' &&
								shopBillingCycle === subscriptionDurationOptions[activeTabIndex].duration
							}
							duration={subscriptionDurationOptions[activeTabIndex].duration}
							discount={
								subscriptionDurationOptions[activeTabIndex].duration > 1
									? subscriptionDurationOptions[activeTabIndex].discount
									: overThousandMR
									? 0
									: 0.5
							}
							onPlanClick={handlePlanClick}
						/>
					</Grid>
					<Grid item xs={12} md={6} lg={4}>
						<PlanBlock
							plan="GROW"
							active={
								shopPlan === 'GROW' &&
								shopBillingCycle === subscriptionDurationOptions[activeTabIndex].duration
							}
							duration={subscriptionDurationOptions[activeTabIndex].duration}
							discount={
								subscriptionDurationOptions[activeTabIndex].duration > 1
									? subscriptionDurationOptions[activeTabIndex].discount
									: overThousandMR
									? 0
									: 0.5
							}
							onPlanClick={handlePlanClick}
						/>
					</Grid>
					<Grid item xs={12} md={6} lg={4}>
						<PlanBlock
							plan="ADVANCED"
							active={
								shopPlan === 'ADVANCED' &&
								shopBillingCycle === subscriptionDurationOptions[activeTabIndex].duration
							}
							duration={subscriptionDurationOptions[activeTabIndex].duration}
							discount={
								subscriptionDurationOptions[activeTabIndex].duration > 1
									? subscriptionDurationOptions[activeTabIndex].discount
									: overThousandMR
									? 0
									: 0.5
							}
							onPlanClick={handlePlanClick}
						/>
					</Grid>
				</Grid>
				<Box
					display="flex"
					flexDirection="row"
					justifyContent="center"
					alignItems="center"
					sx={{
						mt: 7,
						width: '100%',
					}}
				>
					{!isLitePlan(shopPlan) && (
						<RRLink to={Routes.PlanConfirm('LITE')}>
							<Typography
								sx={{
									textDecoration: 'underline',
									color: (theme) => theme.palette.primary.main,
									mr: 4,
									textDecorationColor: 'rgba(38, 1, 120, 0.4)',
									'&:hover': {
										textDecorationColor: (theme) => theme.palette.primary.main,
									},
								}}
							>
								{t('plans.downgradeLite', 'Downgrade to Rentle Free')}
							</Typography>
						</RRLink>
					)}
					<Link href="https://www.rentle.io/enterprise" target="_blank" rel="noopener noreferrer">
						<Typography>
							{t('plans.upgradeEnterprise', 'Get started with Rentle Enterprise')}
						</Typography>
					</Link>
				</Box>
				<PlansLearnMoreLink />
			</Container>
			{isRequestYearlyQuotaModalOpen && !!currentPlan && (
				<RequestYearlyQuotaModal
					isOpen={isRequestYearlyQuotaModalOpen}
					subscriptionDuration={subscriptionDurationOptions[activeTabIndex]}
					plan={currentPlan}
					onCancel={() => setIsRequestYearlyQuotaModalOpen(false)}
					onClose={() => setIsRequestYearlyQuotaModalOpen(false)}
				/>
			)}
		</>
	);
};

export default ChangePlan;
